import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilX } from "@coreui/icons";

import { apiClient } from "services/Api";
import { jwtDecode } from "jwt-decode";
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from "components/globals/AlertNotification";

const IdeaBankList = () => {
  const navigate = useNavigate();
  const [innovativeIdeas, setInnovativeIdeas] = useState([]);
  const [previewIdea, setPreviewIdea] = useState(null);
  const location = useLocation();

  const token = localStorage.getItem("access_token");
  const decodedToken = jwtDecode(token);
  const is_director = decodedToken.is_director;
  const user_id = decodedToken.user_id;

  useEffect(() => {
    const fetchInnovativeIdeas = async () => {
      try {
        const response = await apiClient.get(
          `/innovative-ideas/?all_years=true&state=${encodeURIComponent("Executar Futuramente")}`,
        );
        setInnovativeIdeas(response.data.innovative_ideas);
        setPreviewIdea((current) =>
          current
            ? response.data.innovative_ideas.find(
                (idea) => idea.id === current.id,
              ) || null
            : null,
        );
      } catch (error) {
        console.error("Error fetching ideas:", error);
      }
    };

    fetchInnovativeIdeas();
  }, [location.pathname]);

  const renderText = (text) => (
    <div dangerouslySetInnerHTML={{ __html: text || "" }} />
  );

  const getCodeSortValue = (row) => {
    const [sequencePart, yearPart] = (row.code || "").split("/");
    const sequence = Number(sequencePart);
    const year = Number(yearPart);

    if (!Number.isFinite(sequence) || !Number.isFinite(year)) {
      return row.code || "";
    }

    return `${year}${String(sequence).padStart(4, "0")}`;
  };

  const columns = [
    {
      id: "code",
      name: "Código",
      selector: (row) => getCodeSortValue(row),
      cell: (row) => row.code,
      sortable: true,
      className: "text-center",
      width: "16.6667%",
    },
    {
      name: "Ano",
      selector: (row) => row.year,
      sortable: true,
      className: "text-center",
      width: "16.6667%",
    },
    {
      name: "Descrição",
      selector: (row) => row.designation,
      sortable: true,
      width: "50%",
    },
    {
      name: "Data da Avaliação",
      selector: (row) => row.evaluation_date,
      sortable: true,
      width: "16.6667%",
    },
  ];

  return (
    <>
      <AlertNotification />
      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Banco de ideias</h3>
        </CCardHeader>
        <CCardBody>
          <DataTableComponent
            columns={columns}
            data={innovativeIdeas}
            defaultSortFieldId="code"
            defaultSortAsc={false}
            onRowClicked={setPreviewIdea}
          />
        </CCardBody>
      </CCard>

      {previewIdea && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewIdea(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewIdea)}
        onHide={() => setPreviewIdea(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Ideia inovadora</div>
              <div className="text-medium-emphasis small">
                {previewIdea?.code || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewIdea &&
              (previewIdea.responsible == user_id || is_director) && (
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(`/ideias-inovadoras/${previewIdea.id}`)
                  }
                >
                  <CIcon icon={cilPencil} className="me-1" />
                  Editar
                </CButton>
              )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewIdea(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewIdea && (
            <div className="d-flex flex-column gap-3">
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Ano
                </div>
                <div className="fw-semibold">{previewIdea.year || "-"}</div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Descrição
                </div>
                <div className="fw-semibold">
                  {previewIdea.designation || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Avaliação
                </div>
                <div className="fw-semibold">
                  {previewIdea.evaluation_score ?? "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Riscos
                </div>
                {renderText(previewIdea.risks)}
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Ações/Aquisições Expectáveis
                </div>
                {renderText(previewIdea.actions)}
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Data da Avaliação
                </div>
                <div className="fw-semibold">
                  {previewIdea.evaluation_date || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Observações
                </div>
                {renderText(previewIdea.obs)}
              </div>
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default IdeaBankList;
